import React from 'react';

import { Checkbox, Flex, Form } from 'antd-v5';
import { FormattedMessage } from 'react-intl';
import { Button, DatePicker } from 'scouting-ui';

import { luxonViewDateFormat } from '@shared/constants';
import { intl } from '@shared/localization';
import { isAfterToday } from '@utils/datesLuxon';

import { AdvancementAction, FormField } from '../AdvancementDetails/types';
import styles from './RequirementsRenderer.module.scss';
import { useRequirementsContext } from './context';
import type {
  RequirementFormValues,
  RequirementNode,
  RequirementRowPolicy,
} from './types';
import {
  REQUIREMENTS_FIELD,
  noFutureDateRule,
  requirementNamePaths,
} from './utils';

interface RequirementControlsProps {
  requirement: RequirementNode;
  /** Resolved by the row, which also needs it to decide whether to render this */
  policy: RequirementRowPolicy;
  /** Already bound to the row's top-level ancestor by the caller */
  onSave?: (
    id: number,
    values: RequirementFormValues,
  ) => void | Promise<unknown>;
}

/**
 * The date + approve pair for one requirement, plus a Save that submits only this row
 */
export default function RequirementControls(props: RequirementControlsProps) {
  const { requirement, policy, onSave } = props;
  const form = Form.useFormInstance();

  const { savingRequirementId, disabled, disabledDate, completionDateRules } =
    useRequirementsContext();
  const saving = savingRequirementId === requirement.id;

  const key = String(requirement.id);
  const paths = requirementNamePaths(requirement.id);
  const datePath = [REQUIREMENTS_FIELD, key, FormField.CompletionDate];

  const showApprove = !policy.hideApprove;

  const policyDateDisabled = Boolean(
    policy.disabled[AdvancementAction.CompletionDate],
  );
  const policyApproveDisabled = Boolean(
    policy.disabled[AdvancementAction.Approve],
  );
  const dateDisabled = policyDateDisabled || Boolean(disabled);
  const approveDisabled = policyApproveDisabled || Boolean(disabled);

  // Scoped to this row to improve re-rendering, never scope to a global useWatch
  const approved = Form.useWatch(
    [REQUIREMENTS_FIELD, key, FormField.Approved],
    form,
  );
  const completionDate = Form.useWatch(datePath, form);
  // DO NOT drop the `useWatch` calls above. `isFieldsTouched` will require them
  const dirty = form.isFieldsTouched(paths);

  const canSave =
    !policyDateDisabled || (showApprove && !policyApproveDisabled);

  const handleSave = async () => {
    try {
      await form.validateFields(paths);
    } catch {
      // antd renders the field errors; nothing to add
      return;
    }

    try {
      await onSave?.(
        requirement.id,
        form.getFieldValue([REQUIREMENTS_FIELD, key]),
      );
      // don't use form.resetFields, it would instead restore the values captured at mount, undoing the save
      form.setFields(paths.map(name => ({ name, touched: false })));
    } catch (err) {
      // the container reports the failure; the row stays dirty and re-savable
      console.warn('could not submit requirement', err);
    }
  };

  return (
    <Flex wrap align="center" gap="middle" className={styles.controls}>
      {/* No label: the placeholder inside the input already names the field */}
      <Form.Item
        name={[REQUIREMENTS_FIELD, key, FormField.CompletionDate]}
        className={styles.dateItem}
        // backs up `disabledDate`, which cannot police a value already on the record
        rules={[noFutureDateRule(), ...(completionDateRules ?? [])]}
      >
        <DatePicker
          className={styles.datePicker}
          format={luxonViewDateFormat}
          disabled={dateDisabled}
          disabledDate={date =>
            disabledDate ? disabledDate(date, requirement) : isAfterToday(date)
          }
          placeholder={intl.formatMessage({
            id: 'requirementsRenderer.completionDate',
          })}
        />
      </Form.Item>

      {showApprove && (
        <Form.Item
          name={[REQUIREMENTS_FIELD, key, FormField.Approved]}
          valuePropName="checked"
          dependencies={[datePath]}
          className={styles.approveItem}
          rules={[
            {
              validator: (_rule, checked) => {
                if (!checked) return Promise.resolve();
                return form.getFieldValue(datePath)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        intl.formatMessage({
                          id: 'requirementsRenderer.approveNeedsDate',
                        }),
                      ),
                    );
              },
            },
          ]}
        >
          <Checkbox disabled={approveDisabled}>
            <FormattedMessage id="requirementsRenderer.markAsApproved" />
          </Checkbox>
        </Form.Item>
      )}

      {canSave && (
        <Button
          type="primary"
          onClick={handleSave}
          loading={saving}
          className={styles.save}
          // a checked approve with no date can never be saved
          disabled={
            !dirty ||
            Boolean(disabled) ||
            (Boolean(approved) && !completionDate)
          }
        >
          <FormattedMessage id="requirementsRenderer.save" />
        </Button>
      )}
    </Flex>
  );
}
